<script setup lang="ts">
import { optimizeImage } from '@/composables/elements.client'

interface Props {
  size?: 'sm' | 'md' | 'lg'
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
})

defineOptions({
  name: 'UserAvatar',
})

const user = useUser()

// Get user's first name
function getFirstName(fullName: string | null | undefined): string {
  if (!fullName)
    return 'User'
  return fullName.split(' ')[0]
}

const sizeClasses = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'w-8 h-8'
    case 'lg':
      return 'w-12 h-12'
    default:
      return 'w-10 h-10'
  }
})

const optimizedAvatar = computed(() => {
  if (!user.value?.avatar)
    return null

  const multiplier = props.size === 'lg' ? 48 : props.size === 'md' ? 40 : 32
  return optimizeImage(user.value.avatar, {
    width: multiplier * 2,
    height: multiplier * 2,
  })
})
</script>

<template>
  <div class="avatar-wrapper" :class="sizeClasses">
    <img
      v-if="optimizedAvatar"
      loading="lazy"
      :src="optimizedAvatar"
      referrerpolicy="no-referrer"
      :alt="user?.name || 'User'"
      class="avatar-image"
    >
    <div
      v-else
      class="avatar-placeholder"
    >
      <span class="text-sm font-medium">
        {{ getFirstName(user?.name)?.[0]?.toUpperCase() || 'U' }}
      </span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.avatar-wrapper {
  @apply
    flex
    justify-center
    items-center
    shrink-0
    rounded-full;
}

.avatar-image {
  @apply
    rounded-full
    w-full
    h-full
    object-cover
    object-center;
}

.avatar-placeholder {
  @apply
    w-full
    h-full
    rounded-full
    bg-gray-200
    flex
    items-center
    justify-center
    text-gray-600;
}
</style>
